import {
  AnimatedLoadingIcon,
  Container,
  TicketFillOffIcon,
  Typography
} from "@eventbrite/marmalade"
import { FC, ReactNode, useEffect, useState } from "react"
import { Button as MarmaladeButton } from "@eventbrite/marmalade"
import classNames from "classnames"

// utils
import { getBaseUrl } from "../../../../utils/general_utils"
import {
  customRendererToRemovePTag,
  renderRichText
} from "../../../../utils/contentful/render_rich_text"
import { handleMobileAppClick } from "../../../../utils/personalization/shared"

// types
import { EventbriteOrder } from "../../../../utils/auth/enrich_user_data"

// styles
import * as MyTicketsStyles from "./MyTickets.module.scss"

import placeholderSvgImg from "../../../../../static/images/event_placeholder_image.svg"
import { useSelector } from "react-redux"
import { RootState } from "../../../../store/store"

type TrimmedSingleOrder = {
  title: string
  imageUrl: string
  displayDate: string
  ticketPageUrl: string
}

type ContentfulContent =
  | {
      noTicketsFoundContent: {
        eventActionComplexHeader: ReactNode
        eventActionComplexPrimaryCta: ReactNode
        eventActionComplexSecondaryCta: ReactNode
        eventActionComplexTertiaryCta: ReactNode
      }
      ticketsFoundActionComplex: {
        eventActionComplexHeader: ReactNode
        eventActionComplexPrimaryCta: ReactNode
        eventActionComplexSecondaryCta: ReactNode
        eventActionComplexTertiaryCta: ReactNode
      }
      messageNoUpcomingOrders: ReactNode
      searchingForTickets: ReactNode
    }
  | Record<string, never>

export type Props = {
  noTicketsFoundActionComplex: Queries.HomepageDataQuery["noTicketsFoundActionComplex"]
  ticketsFoundActionComplex: Queries.HomepageDataQuery["ticketsFoundActionComplex"]
  messageNoUpcomingOrders: Queries.HomepageDataQuery["messageNoUpcomingOrders"]
  searchingForTickets: Queries.HomepageDataQuery["searchingForTickets"]
  orders?: EventbriteOrder[]
  columnLayout?: boolean
  isLoadingOrders?: boolean
}

export const MyTickets: FC<Props> = ({
  columnLayout,
  messageNoUpcomingOrders,
  noTicketsFoundActionComplex,
  orders,
  ticketsFoundActionComplex,
  searchingForTickets
}) => {
  const [trimmedOrders, setTrimmedOrders] = useState<TrimmedSingleOrder[]>([])
  const activeOrder = trimmedOrders.length > 0 ? trimmedOrders[0] : null
  const [contentfulContent, setContentfulContent] = useState<ContentfulContent>(
    {}
  )
  const hasAttemptedLogin = useSelector(
    (state: RootState) => state.metaData.hasAttemptedLogin
  )

  useEffect(() => {
    const x: ContentfulContent = Object.assign(contentfulContent)

    if (noTicketsFoundActionComplex) {
      x.noTicketsFoundContent = {
        eventActionComplexHeader:
          noTicketsFoundActionComplex.eventActionComplexHeader,
        eventActionComplexPrimaryCta: renderRichText(
          noTicketsFoundActionComplex?.eventActionComplexPrimaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        ),
        eventActionComplexSecondaryCta: renderRichText(
          noTicketsFoundActionComplex?.eventActionComplexSecondaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        ),
        eventActionComplexTertiaryCta: renderRichText(
          noTicketsFoundActionComplex?.eventActionComplexTertiaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        )
      }
    }

    if (ticketsFoundActionComplex) {
      x.ticketsFoundActionComplex = {
        eventActionComplexHeader:
          ticketsFoundActionComplex.eventActionComplexHeader,
        eventActionComplexPrimaryCta: renderRichText(
          ticketsFoundActionComplex?.eventActionComplexPrimaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        ),
        eventActionComplexSecondaryCta: renderRichText(
          ticketsFoundActionComplex?.eventActionComplexSecondaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        ),
        eventActionComplexTertiaryCta: renderRichText(
          ticketsFoundActionComplex?.eventActionComplexTertiaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        )
      }
    }

    if (messageNoUpcomingOrders) {
      x.messageNoUpcomingOrders = renderRichText(
        messageNoUpcomingOrders.microcopyValue?.raw ?? "",
        customRendererToRemovePTag
      )
    }

    if (searchingForTickets) {
      x.searchingForTickets = renderRichText(
        searchingForTickets?.eventActionComplexLoadingMessage?.microcopyValue
          ?.raw ?? "",
        customRendererToRemovePTag
      )
    }
    setContentfulContent(x)
  }, [
    noTicketsFoundActionComplex,
    ticketsFoundActionComplex,
    messageNoUpcomingOrders,
    orders
  ])

  useEffect(() => {
    if (orders && orders?.length > 0) {
      const filteredOrders: TrimmedSingleOrder[] = orders.map((x) => {
        const singleOrder = {} as TrimmedSingleOrder

        if (x.event?.logo?.url) {
          singleOrder.imageUrl = x.event.logo.url
        }
        if (x?.event?.name?.text) {
          singleOrder.title = x.event.name.text
        }
        if (x.event?.start?.utc) {
          singleOrder.displayDate = formatUTCDate(x.event.start.utc)
        }
        if (x.id) {
          singleOrder.ticketPageUrl = `${
            getBaseUrl() ? getBaseUrl() : process.env.GATSBY_PRODUCT_SITE_URL
          }/mytickets/${x.id}/`
        }

        return singleOrder
      })

      setTrimmedOrders(filteredOrders)
    } else {
      // if we have an empty array of tickets '[]' or if we for some reason receive a falsey value e.g. null, or undefined then we want to just set an empty array for orders
      setTrimmedOrders([])
    }
  }, [orders])

  if (!hasAttemptedLogin) {
    return (
      <Container
        className={classNames(MyTicketsStyles.myticketsOuterContainer, {
          [`${MyTicketsStyles.myticketsColumnContainer}`]: columnLayout
            ? true
            : false
        })}
      >
        <Typography
          className={MyTicketsStyles.myticketsTitle}
          variant="heading-sm"
          color="neutral-1000"
        >
          {contentfulContent?.noTicketsFoundContent?.eventActionComplexHeader
            ? contentfulContent?.noTicketsFoundContent?.eventActionComplexHeader
            : "My tickets"}
        </Typography>

        <div style={{ textAlign: "center", padding: "2rem 0" }}>
          <AnimatedLoadingIcon
            className={MyTicketsStyles.loaderSvg}
            color="#3659E3"
          />
          <Typography
            variant="body-sm"
            style={{ marginTop: "1rem", color: "#C1BDC7" }}
          >
            {contentfulContent?.searchingForTickets
              ? contentfulContent.searchingForTickets
              : "Searching for your tickets..."}
          </Typography>
        </div>
      </Container>
    )
  }

  if (!orders || orders?.length <= 0) {
    return (
      <Container
        className={classNames(MyTicketsStyles.myticketsOuterContainer, {
          [`${MyTicketsStyles.myticketsColumnContainer}`]: columnLayout
            ? true
            : false
        })}
      >
        <div className={MyTicketsStyles.myticketsTitleWrapper}>
          <Typography
            className={MyTicketsStyles.myticketsTitle}
            variant="heading-sm"
            color="neutral-1000"
          >
            {contentfulContent?.noTicketsFoundContent?.eventActionComplexHeader
              ? contentfulContent?.noTicketsFoundContent
                  ?.eventActionComplexHeader
              : "My tickets"}
          </Typography>
        </div>

        <div className={MyTicketsStyles.noTicketsTopContainer}>
          <TicketFillOffIcon size="medium" color="#3A3247" />
          <Typography variant="body-lg">
            {contentfulContent?.messageNoUpcomingOrders
              ? contentfulContent.messageNoUpcomingOrders
              : "No upcoming orders"}
          </Typography>
          <OrdersBtn
            heapId="link.attendee-component.view-past-orders"
            className={MyTicketsStyles.noTicketsPastOrdersBtn}
          >
            {contentfulContent?.noTicketsFoundContent
              ?.eventActionComplexTertiaryCta
              ? contentfulContent.noTicketsFoundContent
                  .eventActionComplexTertiaryCta
              : "View past orders"}
          </OrdersBtn>
        </div>
        <div
          className={classNames(MyTicketsStyles.noTicketsBottomContainer, {
            [`${MyTicketsStyles.noTicketsFindMyTicketsColumnWrapper}`]:
              columnLayout ? true : false
          })}
        >
          <div
            className={MyTicketsStyles.buttonDiv}
            onClick={(e) =>
              handleMobileAppClick(
                `${
                  getBaseUrl()
                    ? getBaseUrl()
                    : process.env.GATSBY_PRODUCT_SITE_URL
                }/find-tickets/`,
                e
              )
            }
            onTouchStart={(e) => e.preventDefault()}
            onTouchEnd={(e) => {
              e.preventDefault()
              handleMobileAppClick(
                `${
                  getBaseUrl()
                    ? getBaseUrl()
                    : process.env.GATSBY_PRODUCT_SITE_URL
                }/find-tickets/`,
                e
              )
            }}
            data-heap-id="button.attendee-component.view-orders"
            style={{ cursor: "pointer" }}
          >
            <MarmaladeButton variant="primary">
              {contentfulContent?.noTicketsFoundContent
                ?.eventActionComplexPrimaryCta
                ? contentfulContent.noTicketsFoundContent
                    .eventActionComplexPrimaryCta
                : "Find my tickets"}
            </MarmaladeButton>
          </div>
          <div
            className={MyTicketsStyles.buttonDiv}
            onClick={(e) =>
              handleMobileAppClick(
                `${
                  getBaseUrl()
                    ? getBaseUrl()
                    : process.env.GATSBY_PRODUCT_SITE_URL
                }/d/`,
                e
              )
            }
            onTouchStart={(e) => e.preventDefault()}
            onTouchEnd={(e) => {
              e.preventDefault()
              handleMobileAppClick(
                `${
                  getBaseUrl()
                    ? getBaseUrl()
                    : process.env.GATSBY_PRODUCT_SITE_URL
                }/d/`,
                e
              )
            }}
            data-heap-id="button.attendee-component.city-browse"
            style={{ cursor: "pointer" }}
          >
            <MarmaladeButton variant="secondary">
              {contentfulContent?.noTicketsFoundContent
                ?.eventActionComplexSecondaryCta
                ? contentfulContent.noTicketsFoundContent
                    .eventActionComplexSecondaryCta
                : "Browse events"}
            </MarmaladeButton>
          </div>
        </div>
      </Container>
    )
  }

  return (
    <Container
      className={classNames(MyTicketsStyles.myticketsOuterContainer, {
        [`${MyTicketsStyles.myticketsColumnContainer}`]: columnLayout
          ? true
          : false
      })}
    >
      <div className={MyTicketsStyles.myticketsTitleWrapper}>
        <Typography
          className={MyTicketsStyles.myticketsTitle}
          variant="heading-sm"
          color="neutral-1000"
        >
          {contentfulContent?.ticketsFoundActionComplex
            ?.eventActionComplexHeader
            ? contentfulContent?.ticketsFoundActionComplex
                ?.eventActionComplexHeader
            : "My tickets"}
        </Typography>

        <OrdersBtn
          className={MyTicketsStyles.myticketsAllOrdersBtnDesktop}
          style={{
            display: columnLayout ? "none" : "inline-block"
          }}
          heapId="button.attendee-component.view-orders-desktop"
        >
          {contentfulContent?.ticketsFoundActionComplex
            ?.eventActionComplexTertiaryCta
            ? contentfulContent?.ticketsFoundActionComplex
                ?.eventActionComplexTertiaryCta
            : "View all orders"}
        </OrdersBtn>
      </div>

      {activeOrder ? (
        <div className={MyTicketsStyles.myticketsPrimaryWrapper}>
          <div className={MyTicketsStyles.myticketsHeaderImageContainer}>
            <div
              onClick={(e) =>
                handleMobileAppClick(activeOrder.ticketPageUrl, e)
              }
              onTouchStart={(e) => e.preventDefault()}
              onTouchEnd={(e) => {
                e.preventDefault()
                handleMobileAppClick(activeOrder.ticketPageUrl, e)
              }}
              data-heap-id="img.attendee-component.image"
              className={MyTicketsStyles.aspectRatioBox}
              style={{ cursor: "pointer" }}
            >
              {activeOrder?.imageUrl ? (
                <img
                  className={MyTicketsStyles.myticketsHeaderImage}
                  src={activeOrder.imageUrl}
                  alt={`Image for event: ${activeOrder.title}`}
                />
              ) : (
                <img
                  className={MyTicketsStyles.myticketsHeaderImage}
                  src={placeholderSvgImg}
                  alt={`Image for event: ${activeOrder.title}`}
                />
              )}
            </div>
          </div>

          <div className={MyTicketsStyles.myticketsContentContainer}>
            <Typography
              variant="body-lg"
              className={MyTicketsStyles.myticketsEventName}
              color="neutral-1000"
            >
              {activeOrder.title}
            </Typography>
            <Typography variant="body-md-bold">
              {activeOrder.displayDate}
            </Typography>

            <div className={MyTicketsStyles.myticketsViewTicketsBtn}>
              <div
                onClick={(e) =>
                  handleMobileAppClick(activeOrder.ticketPageUrl, e)
                }
                onTouchStart={(e) => e.preventDefault()}
                onTouchEnd={(e) => {
                  e.preventDefault()
                  handleMobileAppClick(activeOrder.ticketPageUrl, e)
                }}
                data-heap-id="button.attendee-component.view-tickets"
                style={{ cursor: "pointer" }}
              >
                <MarmaladeButton variant="primary">
                  {contentfulContent?.ticketsFoundActionComplex
                    ?.eventActionComplexPrimaryCta
                    ? contentfulContent?.ticketsFoundActionComplex
                        ?.eventActionComplexPrimaryCta
                    : "View tickets"}
                </MarmaladeButton>
              </div>
            </div>
            <div
              className={MyTicketsStyles.myticketsAllOrdersBtnMobile}
              style={{
                display: columnLayout ? "inline-block" : ""
              }}
            >
              <OrdersBtn>
                {contentfulContent?.ticketsFoundActionComplex
                  ?.eventActionComplexTertiaryCta
                  ? contentfulContent?.ticketsFoundActionComplex
                      ?.eventActionComplexTertiaryCta
                  : "View all orders"}
              </OrdersBtn>
            </div>
          </div>
        </div>
      ) : null}
    </Container>
  )
}

/**
 *
 * @param utcString "2025-02-23T16:00:00Z"
 * @returns "Sun, Feb 23, 10:00 AM"
 */
export const formatUTCDate = (utcString: string) => {
  const date = new Date(utcString)

  return (
    new Intl.DateTimeFormat("en-US", {
      weekday: "short", // Fri
      month: "short", // May
      day: "2-digit", // 26
      hour12: true // am/pm format
    }).format(date) +
    " • " +
    new Intl.DateTimeFormat("en-US", {
      hour: "numeric", // 10
      minute: "2-digit", // 30
      hour12: true // am/pm format
    }).format(date)
  )
}

const OrdersBtn: FC<{
  children: ReactNode
  heapId?: string
  className?: string
  style?: React.CSSProperties
}> = ({ className, children, heapId, style }) => {
  const myTicketsUrl = `${
    getBaseUrl() ? getBaseUrl() : process.env.GATSBY_PRODUCT_SITE_URL
  }/mytickets/`

  return (
    <div
      onClick={(e) => handleMobileAppClick(myTicketsUrl, e)}
      onTouchStart={(e) => e.preventDefault()}
      onTouchEnd={(e) => {
        e.preventDefault()
        handleMobileAppClick(myTicketsUrl, e)
      }}
      style={{ ...style, cursor: "pointer" }}
      data-heap-id={heapId ? heapId : ""}
    >
      <MarmaladeButton variant="ghost" className={classNames(className)}>
        {children}
      </MarmaladeButton>
    </div>
  )
}
